/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { classNames } from '@ember-decorators/component';

@classNames('layout-column layout-align-start-center')
export default class GoalsProgressChart extends Component {
  // Services
  @service
  colours;

  @service
  store;

  @service
  strings;

  // Props
  // category provides the category model for which the chart will be generated
  // for.
  category = null;

  // categoryExpectation provides the category the student is expected to be
  // working on in order to mark the chart as actively working on.
  categoryExpectation = null;

  // categorySummary provides a summary of the number of goals not set,
  // developing, total number and secured within the category.
  @tracked categorySummary = null;

  // loading provides a way to show the chart as loading while waiting on the
  // required chart data.
  loading = false;

  // personId provides the person's identifier.
  personId = '';

  // summary provides the data to use to generate the goal progress chart from.
  summary = null;

  // showCurrentGoalProgress specifies whether the student's current individual
  // goal progress should be displayed to the user.
  showCurrentGoalProgress = false;

  width = 100;
  height = 100;
  thickness = 28;
  smallLabel = false;
  progressDialogOpen = false;

  @alias('category.subject.isGradable')
  isGradable;

  @computed('category.subject.{possibleGrades.[],defaultGrades.[]}')
  get possibleGrades() {
    let possibleGrades = this.get('category.subject.possibleGrades');
    // Fall back to the default grades for empty/missing subject-level grades
    // so that any grades set before removal of a subject-level grade are
    // still included in the progress chart.
    return this.get('category.subject.defaultGrades').map((grade) => {
      return findBy(possibleGrades, 'value', grade.value) || grade;
    });
  }

  @computed('possibleGrades.[]')
  get gradeLabels() {
    return this.possibleGrades.reduce((labels, grade) => {
      labels[grade.value] = grade.string;
      return labels;
    }, {});
  }

  // chartData generates the required chart formatted data.
  get chartData() {
    let data = [];
    const placeholder = {
      value: 'unset',
      tooltip: 'Not set',
      count: 1,
      color: '#e0e0e0',
    };

    let summary = this.categorySummary;
    if (isEmpty(summary)) {
      // No data - return a dummy entry so that a chart is still generated
      data.push(placeholder);
      return data;
    }

    if (this.isGradable) {
      this.possibleGrades.forEach((grade) => {
        if (summary.byGrade[grade.value]) {
          data.push({
            value: `${grade.value}`,
            tooltip: grade.string || `${grade.value}`,
            count: summary.byGrade[grade.value],
            color: grade.colour,
          });
        }
      });
      let numUngraded = summary.get('ungraded.secure');
      if (numUngraded) {
        data.push({
          value: 'secure',
          tooltip: 'Secure (not yet graded)',
          count: numUngraded,
          color: '#66bb6a',
          hatched: true,
        });
      }
    } else if (summary.secure) {
      data.push({
        value: 'secure',
        tooltip: 'Secure',
        count: summary.secure,
        color: '#66bb6a',
      });
    }

    let numDeveloping = summary.developing;
    if (this.isGradable) {
      numDeveloping = summary.get('ungraded.developing');
    }
    if (numDeveloping) {
      data.push({
        value: 'developing',
        tooltip: 'Developing',
        count: numDeveloping,
        color: '#ffecb3',
        hatched: this.isGradable,
      });
    }

    let numNotSet = summary.notSet;
    if (this.isGradable) {
      numNotSet = summary.get('ungraded.notSet');
    }
    if (numNotSet) {
      data.push({
        value: 'unset',
        tooltip: 'Not set',
        count: numNotSet,
        color: '#e0e0e0',
      });
    }

    return data;
  }

  // complete calculates if the given category has all goals secure.
  get complete() {
    let summary = this.categorySummary;
    return (
      // Require a summary.
      !isEmpty(summary) &&
      // Must have secured (completed) at least one goal.
      summary.secure > 0 &&
      // Number of secure goals must at least equal number in category.
      summary.secure >= summary.totalGoals &&
      // Must not be any "not set" goals in the category.
      summary.notSet === 0 &&
      // Must not be any "developing" goals in the category.
      summary.developing === 0
    );
  }
}
